import { useState, useEffect, useMemo } from 'react';
import { updateDirectionBasedOnLanguage } from '@localisation/localizationUtils';
/**
 * Auth-specific localization function
 * Checks window.UXPMessages first (populated by auth config), then falls back to registered messages
 *
 * @param code - Localization code (e.g., 'uxp-core.auth.log-in')
 * @param language - Language code (e.g., 'en', 'ar-sa')
 * @param params - Optional parameters to replace in the message (e.g., { accountName: 'Iviva' })
 * @returns Localized message or the code if not found
 */
export function $L(code, language, params) {
    // Check UXPMessages first (auth custom messages from config)
    const uxpMessages = window.UXPMessages || {};
    if (uxpMessages[code] && uxpMessages[code][language]) {
        let localizedMessage = uxpMessages[code][language];
        // Replace params
        if (params && Object.keys(params).length > 0) {
            for (const key of Object.keys(params)) {
                localizedMessage = localizedMessage.replace(`$${key}`, params[key]);
            }
        }
        return localizedMessage;
    }
    // Return code if no translation found
    return code;
}
/**
 * Process and populate window.UXPMessages with custom auth messages
 * This allows custom messages from auth config to override default messages
 *
 * @param messages - Custom messages object from auth config
 */
export function populateAuthMessages(messages) {
    if (!messages) {
        return;
    }
    // Get existing UXPMessages
    const existingMessages = window.UXPMessages || {};
    // Merge custom messages (custom messages override defaults)
    // Structure: { "uxp-core.auth.log-in": { "en": "Login", "ar-sa": "..." } }
    for (const code in messages) {
        if (!existingMessages[code]) {
            existingMessages[code] = {};
        }
        // Merge language-specific messages
        existingMessages[code] = Object.assign(Object.assign({}, existingMessages[code]), messages[code]);
    }
    window.UXPMessages = existingMessages;
}
/**
 * Build available languages from context and auth config
 *
 * @param contextLanguages - Full Language[] from context.enabledLanguages
 * @param authConfigLanguageCodes - Optional string[] from authConfig.localization.enabledLanguages to filter
 * @returns Array of Language objects with Name, Code, and Direction
 */
export function buildAvailableLanguages(contextLanguages, authConfigLanguageCodes) {
    // If no context languages provided, return empty
    if (!contextLanguages || contextLanguages.length === 0) {
        return [];
    }
    // If auth config has specific language codes, filter by them
    if (authConfigLanguageCodes && authConfigLanguageCodes.length > 0) {
        return contextLanguages.filter(lang => authConfigLanguageCodes.includes(lang.Code));
    }
    // Otherwise return all context languages
    return contextLanguages;
}
/**
 * Custom hook for managing auth localization
 * Handles language state, direction updates, and message population
 *
 * @param localizationConfig - Auth localization configuration
 * @param contextLanguages - Full Language[] from context.enabledLanguages
 * @returns Object with currentLanguage, availableLanguages, and handleLanguageChange
 */
export function useAuthLocalization(localizationConfig, contextLanguages) {
    // Language state management
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return (localizationConfig === null || localizationConfig === void 0 ? void 0 : localizationConfig.defaultLanguage) || 'en';
    });
    // Track if messages have been loaded
    const [messagesLoaded, setMessagesLoaded] = useState(false);
    // Build available languages from context and config
    const availableLanguages = useMemo(() => {
        return buildAvailableLanguages(contextLanguages, localizationConfig === null || localizationConfig === void 0 ? void 0 : localizationConfig.enabledLanguages);
    }, [contextLanguages, localizationConfig === null || localizationConfig === void 0 ? void 0 : localizationConfig.enabledLanguages]);
    // Populate window.UXPMessages with custom auth messages on mount
    useEffect(() => {
        populateAuthMessages(localizationConfig === null || localizationConfig === void 0 ? void 0 : localizationConfig.messages);
        // Small delay to ensure messages are fully populated
        setTimeout(() => {
            setMessagesLoaded(true);
        }, 50);
    }, [localizationConfig === null || localizationConfig === void 0 ? void 0 : localizationConfig.messages]);
    // Update direction when language changes
    useEffect(() => {
        if (availableLanguages.length > 0) {
            updateDirectionBasedOnLanguage(currentLanguage, availableLanguages);
        }
    }, [currentLanguage, availableLanguages]);
    // Handle language change
    const handleLanguageChange = (languageCode) => {
        setCurrentLanguage(languageCode);
    };
    return {
        currentLanguage,
        availableLanguages,
        handleLanguageChange,
        messagesLoaded
    };
}
